import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTimeoutFn } from 'react-use';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { getLogger, tracker } from 'app-components/utils/core';
import { isDevMode } from 'app-components/utils/core/config';
import { useAppSelector } from 'hooks/redux';

import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';

import * as appSelectors from 'data/app/selectors';
import { activateUpdatedApp, register, setSWUpdateInterval, unregister } from 'swMain';
import { initPushNotificationsIfAccepted } from 'swPushNotifications';

const logger = getLogger('ServiceWorkers');

let isLoadCheckComplete = false;

const ServiceWorkers = () => {
  const serviceWorkerEnable =
    useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'serviceWorkerEnable')) &&
    (!isDevMode() ||
      !(window?.top as any)?.Cypress ||
      window?.location?.hostname.split('.').slice(-2).join('.') !== 'amplifyapp.com');
  const serviceWorkerUpdateInterval = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'serviceWorkerUpdateInterval'),
  );
  const serviceWorkerIsRegistered = useSelector(appSelectors.getServiceWorkerIsRegistered);
  const getServiceWorkerIsRegistering = useSelector(appSelectors.getServiceWorkerIsRegistering);
  const serviceWorkerUpdateIsAvailable: boolean | undefined = useSelector(
    appSelectors.getServiceWorkerUpdateIsAvailable,
  );

  useEffect(() => {
    setSWUpdateInterval(serviceWorkerEnable ? Number(serviceWorkerUpdateInterval) : 0);
  }, [serviceWorkerUpdateInterval, serviceWorkerEnable]);

  useEffect(() => {
    if (serviceWorkerEnable) {
      if (!getServiceWorkerIsRegistering && !serviceWorkerIsRegistered) {
        register();
      }
    } else if (serviceWorkerIsRegistered || getServiceWorkerIsRegistering) {
      logger.debug('Calling unregisterServiceWorker');
      unregister();
      tracker.track(tracker.events.serviceWorker, { action: 'unregistered' });
    }
  }, [serviceWorkerEnable, serviceWorkerIsRegistered, getServiceWorkerIsRegistering]);

  useEffect(() => {
    if (serviceWorkerIsRegistered) {
      initPushNotificationsIfAccepted();
    }
  }, [serviceWorkerIsRegistered]);

  const serviceWorkerUpdateOnNavigation = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'serviceWorkerUpdateOnNavigation'),
  );
  const { pathname } = useLocation();
  const pathL1 = pathname?.split('/')?.[1];
  useEffect(() => {
    if (serviceWorkerEnable && serviceWorkerUpdateIsAvailable && serviceWorkerUpdateOnNavigation) {
      activateUpdatedApp('navigation');
    }
  }, [pathL1]); // eslint-disable-line react-hooks/exhaustive-deps

  const serviceWorkerUserUpdateTimeout = useAppSelector(
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'serviceWorkerUserUpdateTimeout') || 0,
  );
  const serviceWorkerUserUpdateWindow = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'serviceWorkerUserUpdateWindow'),
  );
  const userUpdateTimeout = useMemo(
    () =>
      serviceWorkerUserUpdateTimeout >= 0
        ? serviceWorkerUserUpdateTimeout + Math.trunc(Math.random() * (serviceWorkerUserUpdateWindow || 0))
        : -1,
    [serviceWorkerUserUpdateTimeout, serviceWorkerUserUpdateWindow],
  );
  const [showFinishUpdate, setShowFinishUpdate] = useState(false);
  useTimeoutFn(
    () => {
      if (serviceWorkerEnable && serviceWorkerUpdateIsAvailable && userUpdateTimeout >= 0) {
        setShowFinishUpdate(true);
      }
    },
    serviceWorkerEnable && serviceWorkerUpdateIsAvailable && userUpdateTimeout >= 0 ? userUpdateTimeout : -1,
  );

  const serviceWorkerUpdateOnLoad = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'serviceWorkerUpdateOnLoad'),
  );
  useTimeoutFn(() => {
    isLoadCheckComplete = true;
  }, 1000);
  useEffect(() => {
    if (!isLoadCheckComplete && serviceWorkerUpdateOnLoad && serviceWorkerEnable && serviceWorkerUpdateIsAvailable) {
      isLoadCheckComplete = true;
      activateUpdatedApp('load');
    }
  }, [serviceWorkerEnable, serviceWorkerUpdateIsAvailable, serviceWorkerUpdateOnLoad]);

  const serviceWorkerUpdateOnIdleTimeout = useAppSelector(
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'serviceWorkerUpdateOnIdleTimeout') || 0,
  );
  const serviceWorkerUpdateOnIdleWindow = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'serviceWorkerUpdateOnIdleWindow'),
  );
  const idleTimerTimeout = useMemo(
    () =>
      serviceWorkerUpdateOnIdleTimeout && serviceWorkerUpdateOnIdleTimeout >= 0
        ? serviceWorkerUpdateOnIdleTimeout + Math.trunc(Math.random() * (serviceWorkerUpdateOnIdleWindow || 0))
        : -1,
    [serviceWorkerUpdateOnIdleTimeout, serviceWorkerUpdateOnIdleWindow],
  );
  const idleTimerDisabled = !serviceWorkerEnable || !serviceWorkerUpdateIsAvailable || idleTimerTimeout < 0;
  const onIdle = useCallback(() => activateUpdatedApp('idle'), []);
  useIdleTimer({
    disabled: idleTimerDisabled,
    ...(idleTimerTimeout >= 0 && { timeout: idleTimerTimeout }),
    onIdle,
    stopOnIdle: true,
  });

  const activateUserUpdate = useCallback(() => activateUpdatedApp('user'), []);
  const dismissUserUpdate = useCallback(() => {
    setShowFinishUpdate(false);
    tracker.track(tracker.events.serviceWorker, { action: 'dismissed', method: 'user' });
  }, []);

  return (
    <Snackbar
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      open={showFinishUpdate}
      autoHideDuration={60000}
      message='New app is available'
      action={
        <>
          <Button color='inherit' size='small' onClick={activateUserUpdate}>
            FINISH UPDATE
          </Button>
          <IconButton size='small' aria-label='close' color='inherit' onClick={dismissUserUpdate}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </>
      }
    />
  );
};

export default ServiceWorkers;
